// services/v3/cache/cartStorage.ts — persisted carts (SPEC §5)
// Key layout: `v3:cart:${key}` where key = `${storeId}:${qrcodeId}`.
import type { CartItem, PersistedCart } from '~/types/v3/cart'
import { kvDel, kvGet, kvKeys, kvSet } from './idb'

const CART_PREFIX = 'v3:cart:'
const DAY_MS = 24 * 60 * 60 * 1000

const storageKey = (key: string): string => CART_PREFIX + key

function isPersistedCart(v: unknown): v is PersistedCart {
  if (typeof v !== 'object' || v === null) return false
  const c = v as Record<string, unknown>
  return (
    typeof c.key === 'string' &&
    Array.isArray(c.items) &&
    (typeof c.pendingJobId === 'string' || c.pendingJobId === null) &&
    typeof c.updatedAt === 'number'
  )
}

/** Returns the persisted cart for `key`, or null when absent/corrupt. */
export async function loadCart(key: string): Promise<PersistedCart | null> {
  if (!key) return null
  const raw = await kvGet<unknown>(storageKey(key))
  if (!isPersistedCart(raw)) return null
  return {
    key: raw.key,
    items: (raw.items as CartItem[]) ?? [],
    pendingJobId: raw.pendingJobId ?? null,
    updatedAt: raw.updatedAt,
    v: 1,
  }
}

/** Writes the cart, stamping `updatedAt` and `v`. */
export async function saveCart(cart: PersistedCart): Promise<void> {
  if (!cart || !cart.key) return
  const payload: PersistedCart = {
    key: cart.key,
    items: cart.items ?? [],
    pendingJobId: cart.pendingJobId ?? null,
    updatedAt: typeof cart.updatedAt === 'number' ? cart.updatedAt : Date.now(),
    v: 1,
  }
  await kvSet(storageKey(cart.key), payload)
}

export async function clearCart(key: string): Promise<void> {
  if (!key) return
  await kvDel(storageKey(key))
}

/** Drops carts untouched for longer than `maxAgeMs` (default 24 h). Returns the removed keys. */
export async function pruneCarts(maxAgeMs = DAY_MS): Promise<string[]> {
  const removed: string[] = []
  const now = Date.now()
  const all = await kvKeys()
  for (const storeKey of all) {
    if (!storeKey.startsWith(CART_PREFIX)) continue
    const raw = await kvGet<unknown>(storeKey)
    if (!isPersistedCart(raw)) {
      await kvDel(storeKey)
      removed.push(storeKey.slice(CART_PREFIX.length))
      continue
    }
    if (now - raw.updatedAt > maxAgeMs) {
      await kvDel(storeKey)
      removed.push(storeKey.slice(CART_PREFIX.length))
    }
  }
  return removed
}
